<script setup lang="ts">
import { ref } from 'vue'

import Accordeon, { type AccordeonEmits, type Props as AccordeonProps } from '@/components/Accordion/index.vue'
import { useSidebarStore } from '~~/src/stores/sidebar'
import { useElementalListsStore } from '~~/src/stores/useElementalListsStore'
import type { ElementalTypeEnum } from '~~/src/types/elemental'
import { ElementalMainListContextKey } from '~~/src/components/DrawerSidebarRight/components/shared/ElementalMainListContext'

type Props = {
  listId: string
  listtitle: string
  isLoading?: boolean
  elementalsType: ElementalTypeEnum
  isStatic?: boolean
  showHeader?: boolean
} & AccordeonProps

const props = defineProps<Props>()
const emits = defineEmits<AccordeonEmits>()

defineOptions({ name: 'TableRowAccordion' })
const { state: elementalListsStore, getView } = useElementalListsStore()
const { isMainListOpen, openMainList, closeMainList } = useSidebarStore()

const accordionRef = ref<InstanceType<typeof Accordeon> | null>(null)

const wrapperRef = ref<HTMLElement | null>(null)
const elementalLabelType = getElementalTypeRoute(props.elementalsType)
const isOpen = isMainListOpen(elementalLabelType)

const viewStatus = getView(props.elementalsType)

const show = ref(viewStatus.value?.filterActive)

const fn = useDebounceFn(() => {
  if (elementalListsStore.query.trim() === '')
    return show.value = true

  const count = elementalListsStore.viewsActives?.[props.elementalsType]?.count

  if (count && count < 1) {
    if (accordionRef.value)
      accordionRef.value.toggle(false)
    return show.value = false
  }
  else {
    show.value = true
    if (accordionRef.value)
      accordionRef.value.toggle(true)
  }
}, 300)

watch(() => elementalListsStore.query, () => {
  fn()
})
provide(ElementalMainListContextKey, {
  id: props.listId,
})

const handlerToggle = (isOpen?: boolean) => {
  if (props.elementalsType !== undefined) {
    if (isOpen)
      closeMainList(elementalLabelType)
    else
      openMainList(elementalLabelType)
  }
  emits('onToggle', isOpen)
}

const handlerClose = () => {
  if (props.elementalsType !== undefined)
    closeMainList(elementalLabelType)
  emits('onClose')
}

const handlerOpen = () => {
  if (props.elementalsType !== undefined)
    openMainList(elementalLabelType)
  emits('onOpen')
}

const accWrapperRef = computed(() => accordionRef.value?.wrapperRef)

useDragHover({
  node: accWrapperRef,
  timeActions: {
    '>500': () => {
      accordionRef.value?.toggle()
    },
  },
})

onMounted(() => {
  if (props.isStatic)
    handlerOpen()
})
</script>

<template>
  <div v-show="show && viewStatus?.filterActive" ref="wrapperRef">
    <Accordeon
      ref="accordionRef"
      :ignore-events="elementalListsStore.query.trim() !== ''"
      :is-open="isOpen"
      :hidden-header="props.isStatic"
      @on-close="handlerClose"
      @on-open="handlerOpen"
      @on-toggle="handlerToggle($event)"
    >
      <template v-if="!isStatic" #header>
        <button
          class="w-full"
          @click="() => accordionRef?.toggle()"
        >
          <slot name="header" />
        </button>
      </template>
      <template #content>
        <div :class="$style['no-scrollbar']">
          <slot name="content" />
        </div>
      </template>
    </Accordeon>
  </div>
</template>

<style module lang="css">
.no-scrollbar{
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
